import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  BookOpen, 
  CheckCircle, 
  Clock, 
  Target, 
  Award,
  Heart,
  BarChart3,
  BookMarked,
  BookX,
  ShoppingCart,
  TrendingUp,
  DollarSign,
  Star,
  Users,
  Zap,
  Globe,
  Activity,
  PieChart,
  Target as TargetIcon,
  Calendar,
  BookText,
  Library
} from 'lucide-react';
import { useAppState } from '../context/AppStateContext';

const StatisticsDashboard: React.FC = () => {
  const { state } = useAppState();

  const dashboardData = useMemo(() => {
    const libros = state.libros.filter(book => book.estado !== 'wishlist');
    const coleccionesManga = state.coleccionesManga;
    
    // Métricas principales
    const totalLibros = libros.length;
    const totalManga = coleccionesManga.reduce((sum, c) => sum + c.tomos.length, 0); // Contar tomos individuales, no colecciones
    const totalColeccion = totalLibros + totalManga;
    
    const librosLeidos = libros.filter(book => book.estado === 'leido').length;
    const tomosMangaLeidos = coleccionesManga.reduce((sum, c) => sum + c.tomosLeidos, 0);
    const totalLeidos = librosLeidos + tomosMangaLeidos;
    
    const librosLeyendo = libros.filter(book => book.estado === 'leyendo').length;
    const tomosMangaLeyendo = coleccionesManga.reduce((sum, c) => sum + c.tomos.filter(t => t.estado === 'leyendo').length, 0);
    const totalLeyendo = librosLeyendo + tomosMangaLeyendo;
    
    const librosTBR = libros.filter(book => book.estado === 'tbr').length;
    const tomosMangaTBR = coleccionesManga.reduce((sum, c) => sum + c.tomos.filter(t => t.estado === 'tbr').length, 0);
    const totalTBR = librosTBR + tomosMangaTBR;
    
    // Valor total
    const valorLibros = libros
      .filter(book => book.precio && book.precio > 0)
      .reduce((sum, book) => sum + (book.precio || 0), 0);
    
    const valorManga = coleccionesManga.reduce((sum, c) => {
      if (c.precioPorTomo && c.precioPorTomo > 0) {
        const tomosReales = c.tomos.filter(t => 
          t.fechaCompra || 
          t.estado === 'comprado' || 
          t.estado === 'leido' || 
          t.estado === 'leyendo'
        ).length;
        return sum + (tomosReales * c.precioPorTomo);
      }
      return sum;
    }, 0);
    
    const valorTotal = valorLibros + valorManga;
    
    // Páginas totales
    const paginasLibros = libros
      .filter(book => book.estado === 'leido')
      .reduce((sum, book) => sum + (book.paginas || 0), 0);
    
    const paginasManga = coleccionesManga.reduce((sum, c) => {
      return sum + c.tomos
        .filter(t => t.estado === 'leido')
        .reduce((sumTomo, tomo) => sumTomo + (c.precioPorTomo ? 200 : 0), 0);
    }, 0);
    
    const paginasTotales = paginasLibros + paginasManga;
    
    // Progreso general
    const progresoGeneral = totalColeccion > 0 ? (totalLeidos / totalColeccion) * 100 : 0;
    
    // Objetivo anual
    const objetivoAnual = state.config.objetivoLecturaAnual || 0;
    const progresoObjetivo = objetivoAnual > 0 ? (totalLeidos / objetivoAnual) * 100 : 0;
    
    // Sagas
    const sagasCompletadas = state.sagas.filter(s => s.isComplete).length;
    const sagasActivas = state.sagas.filter(s => !s.isComplete).length;
    
    // Colecciones manga completadas
    const coleccionesMangaCompletadas = coleccionesManga.filter(c => c.isComplete).length;
    
    // Calificación promedio
    const librosConCalificacion = libros.filter(book => book.calificacion && book.calificacion > 0);
    const calificacionPromedioLibros = librosConCalificacion.length > 0 
      ? librosConCalificacion.reduce((sum, book) => sum + (book.calificacion || 0), 0) / librosConCalificacion.length
      : 0;
    
    const tomosConCalificacion = coleccionesManga.reduce((sum, c) => {
      return sum + c.tomos.filter(t => t.calificacion && t.calificacion > 0).length;
    }, 0);
    
    const calificacionPromedioManga = tomosConCalificacion > 0 
      ? coleccionesManga.reduce((sum, c) => {
          const calificaciones = c.tomos
            .filter(t => t.calificacion && t.calificacion > 0)
            .map(t => t.calificacion!);
          return sum + calificaciones.reduce((s, cal) => s + cal, 0);
        }, 0) / tomosConCalificacion
      : 0;
    
    const calificacionPromedioGeneral = (calificacionPromedioLibros + calificacionPromedioManga) / 2;
    
    // Estadísticas por género (top 5)
    const generosCombinados: Record<string, { libros: number; manga: number; total: number }> = {};
    
    libros.forEach(book => {
      if (book.genero) {
        if (!generosCombinados[book.genero]) {
          generosCombinados[book.genero] = { libros: 0, manga: 0, total: 0 };
        }
        generosCombinados[book.genero].libros++;
        generosCombinados[book.genero].total++;
      }
    });
    
    coleccionesManga.forEach(coleccion => {
      if (coleccion.genero) {
        if (!generosCombinados[coleccion.genero]) {
          generosCombinados[coleccion.genero] = { libros: 0, manga: 0, total: 0 };
        }
        generosCombinados[coleccion.genero].manga++;
        generosCombinados[coleccion.genero].total++;
      }
    });
    
    const topGeneros = Object.entries(generosCombinados)
      .map(([genero, stats]) => ({
        genero,
        total: stats.total,
        porcentaje: (stats.total / totalColeccion) * 100
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 5);
    
    // Estadísticas de compra del mes actual
    const mesActual = new Date().getMonth();
    const añoActual = new Date().getFullYear();
    
    const comprasMesActual = libros.filter(book => {
      if (!book.fechaCompra) return false;
      const fechaCompra = new Date(book.fechaCompra);
      return fechaCompra.getMonth() === mesActual && fechaCompra.getFullYear() === añoActual;
    }).length;
    
    const tomosCompradosMesActual = coleccionesManga.reduce((sum, c) => {
      return sum + c.tomos.filter(t => {
        if (!t.fechaCompra) return false;
        const fechaCompra = new Date(t.fechaCompra);
        return fechaCompra.getMonth() === mesActual && fechaCompra.getFullYear() === añoActual;
      }).length;
    }, 0);
    
    const totalComprasMes = comprasMesActual + tomosCompradosMesActual;
    
    // Estadísticas de lectura del mes actual
    const lecturasMesActual = libros.filter(book => {
      if (!book.fechaFin) return false;
      const fechaLectura = new Date(book.fechaFin);
      return fechaLectura.getMonth() === mesActual && fechaLectura.getFullYear() === añoActual;
    }).length;
    
    const tomosLeidosMesActual = coleccionesManga.reduce((sum, c) => {
      return sum + c.tomos.filter(t => {
        if (!t.fechaLecturaReciente) return false;
        const fechaLectura = new Date(t.fechaLecturaReciente);
        return fechaLectura.getMonth() === mesActual && fechaLectura.getFullYear() === añoActual;
      }).length;
    }, 0);
    
    const totalLecturasMes = lecturasMesActual + tomosLeidosMesActual;
    
    return {
      // Totales
      totalLibros,
      totalManga,
      totalColeccion,
      totalLeidos,
      totalLeyendo,
      totalTBR,
      valorTotal,
      paginasTotales,
      
      // Progreso
      progresoGeneral,
      objetivoAnual,
      progresoObjetivo,
      
      // Sagas y colecciones
      sagasCompletadas,
      sagasActivas,
      coleccionesMangaCompletadas,
      
      // Calificaciones
      calificacionPromedioGeneral,
      librosConCalificacion: librosConCalificacion.length,
      tomosConCalificacion,
      
      // Géneros
      topGeneros,
      
      // Mes actual
      totalComprasMes,
      totalLecturasMes,
      comprasMesActual,
      tomosCompradosMesActual,
      lecturasMesActual,
      tomosLeidosMesActual
    };
  }, [state.libros, state.coleccionesManga, state.sagas, state.config.objetivoLecturaAnual]);

  const metricCards = [
    {
      title: 'Total Colección',
      value: dashboardData.totalColeccion,
      icon: Library,
      color: 'from-blue-500 to-blue-600',
      description: `${dashboardData.totalLibros} libros + ${dashboardData.totalManga} tomos`,
      trend: 'up'
    },
    {
      title: 'Progreso General',
      value: `${dashboardData.progresoGeneral.toFixed(1)}%`,
      icon: TrendingUp,
      color: 'from-green-500 to-green-600',
      description: `${dashboardData.totalLeidos} de ${dashboardData.totalColeccion} completados`,
      trend: 'up'
    },
    {
      title: 'Valor Total',
      value: dashboardData.valorTotal.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' }),
      icon: DollarSign,
      color: 'from-emerald-500 to-emerald-600',
      description: 'Inversión total en la colección',
      trend: 'up'
    },
    {
      title: 'Páginas Leídas',
      value: dashboardData.paginasTotales.toLocaleString(),
      icon: BookText,
      color: 'from-purple-500 to-purple-600',
      description: 'Total de páginas consumidas',
      trend: 'up'
    },
    {
      title: 'Objetivo Anual',
      value: `${dashboardData.progresoObjetivo.toFixed(1)}%`,
      icon: Target,
      color: 'from-orange-500 to-orange-600',
      description: `${dashboardData.totalLeidos} de ${dashboardData.objetivoAnual} libros`,
      trend: dashboardData.progresoObjetivo >= 100 ? 'complete' : 'up'
    },
    {
      title: 'Calificación Promedio',
      value: dashboardData.calificacionPromedioGeneral.toFixed(1),
      icon: Star,
      color: 'from-yellow-500 to-yellow-600',
      description: `${dashboardData.librosConCalificacion + dashboardData.tomosConCalificacion} calificaciones`,
      trend: 'stable'
    }
  ];

  const quickStats = [
    {
      title: 'Leyendo Actualmente',
      value: dashboardData.totalLeyendo,
      icon: Clock,
      color: 'bg-yellow-500'
    },
    {
      title: 'Por Leer (TBR)',
      value: dashboardData.totalTBR,
      icon: BookOpen,
      color: 'bg-orange-500'
    },
    {
      title: 'Sagas Completadas',
      value: dashboardData.sagasCompletadas,
      icon: Award,
      color: 'bg-purple-500'
    },
    {
      title: 'Sagas Activas',
      value: dashboardData.sagasActivas,
      icon: Activity,
      color: 'bg-indigo-500'
    },
    {
      title: 'Colecciones Manga Completadas',
      value: dashboardData.coleccionesMangaCompletadas,
      icon: BookMarked,
      color: 'bg-pink-500'
    },
    {
      title: 'Compras del Mes',
      value: dashboardData.totalComprasMes,
      icon: ShoppingCart,
      color: 'bg-green-500'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header del Dashboard */}
      <div className="text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="inline-flex items-center space-x-2 mb-4"
        >
          <BarChart3 className="h-8 w-8 text-blue-500" />
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
            Dashboard de Estadísticas
          </h1>
        </motion.div>
        <p className="text-slate-600 dark:text-slate-400">
          Resumen completo de tu biblioteca en un vistazo
        </p>
      </div>

      {/* Métricas principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {metricCards.map((metric, index) => (
          <motion.div
            key={metric.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="glass-effect rounded-xl p-6 bg-gradient-to-br from-white to-slate-50 dark:from-slate-800 dark:to-slate-700 border border-slate-200 dark:border-slate-600"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg bg-gradient-to-r ${metric.color}`}>
                <metric.icon className="h-6 w-6 text-white" />
              </div>
              <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                metric.trend === 'up' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                metric.trend === 'complete' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' :
                'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300'
              }`}>
                {metric.trend === 'up' ? '↗' : metric.trend === 'complete' ? '✓' : '→'}
              </div>
            </div>
            
            <div className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
              {metric.value}
            </div>
            
            <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {metric.title}
            </div>
            
            <div className="text-xs text-slate-500 dark:text-slate-400">
              {metric.description}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Estadísticas rápidas */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="glass-effect rounded-xl p-6"
      >
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-6 flex items-center">
          <Activity className="h-5 w-5 mr-2 text-blue-500" />
          Estadísticas Rápidas
        </h3>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {quickStats.map((stat, index) => (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.7 + index * 0.1 }}
              className="text-center p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-200"
            >
              <div className={`inline-flex items-center justify-center w-10 h-10 rounded-lg ${stat.color} mb-3`}>
                <stat.icon className="h-5 w-5 text-white" />
              </div>
              <div className="text-xl font-bold text-slate-900 dark:text-white mb-1">
                {stat.value}
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-400 leading-tight">
                {stat.title}
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Top géneros */}
      {dashboardData.topGeneros.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="glass-effect rounded-xl p-6"
        >
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-6 flex items-center">
            <TargetIcon className="h-5 w-5 mr-2 text-green-500" />
            Top Géneros
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {dashboardData.topGeneros.map((genero, index) => (
              <motion.div
                key={genero.genero}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.9 + index * 0.1 }}
                className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                      {index + 1}
                    </div>
                    <span className="font-medium text-slate-900 dark:text-white">
                      {genero.genero}
                    </span>
                  </div>
                  <span className="text-sm text-slate-600 dark:text-slate-400">
                    {genero.total}
                  </span>
                </div>
                
                <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(genero.porcentaje, 100)}%` }}
                  />
                </div>
                
                <div className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                  {genero.porcentaje.toFixed(1)}% de la colección
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Resumen del mes */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.0 }}
        className="glass-effect rounded-xl p-6"
      >
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-6 flex items-center">
          <Calendar className="h-5 w-5 mr-2 text-purple-500" />
          Resumen del Mes Actual
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Compras del mes */}
          <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-700">
            <div className="flex items-center space-x-3 mb-3">
              <ShoppingCart className="h-6 w-6 text-green-600 dark:text-green-400" />
              <h4 className="text-lg font-semibold text-green-800 dark:text-green-200">
                Compras del Mes
              </h4>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-green-700 dark:text-green-300">Libros:</span>
                <span className="font-semibold text-green-800 dark:text-green-200">
                  {dashboardData.comprasMesActual}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-green-700 dark:text-green-300">Tomos de Manga:</span>
                <span className="font-semibold text-green-800 dark:text-green-200">
                  {dashboardData.tomosCompradosMesActual}
                </span>
              </div>
              <div className="flex justify-between border-t border-green-200 dark:border-green-700 pt-2">
                <span className="text-green-700 dark:text-green-300 font-medium">Total:</span>
                <span className="font-bold text-green-800 dark:text-green-200 text-lg">
                  {dashboardData.totalComprasMes}
                </span>
              </div>
            </div>
          </div>
          
          {/* Lecturas del mes */}
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
            <div className="flex items-center space-x-3 mb-3">
              <BookOpen className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              <h4 className="text-lg font-semibold text-blue-800 dark:text-blue-200">
                Lecturas del Mes
              </h4>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-blue-700 dark:text-blue-300">Libros:</span>
                <span className="font-semibold text-blue-800 dark:text-blue-200">
                  {dashboardData.lecturasMesActual}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-700 dark:text-blue-300">Tomos de Manga:</span>
                <span className="font-semibold text-blue-800 dark:text-blue-200">
                  {dashboardData.tomosLeidosMesActual}
                </span>
              </div>
              <div className="flex justify-between border-t border-blue-200 dark:border-blue-700 pt-2">
                <span className="text-blue-700 dark:text-blue-300 font-medium">Total:</span>
                <span className="font-bold text-blue-800 dark:text-blue-200 text-lg">
                  {dashboardData.totalLecturasMes}
                </span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Indicadores de rendimiento */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.1 }}
        className="glass-effect rounded-xl p-6"
      >
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-6 flex items-center">
          <TrendingUp className="h-5 w-5 mr-2 text-orange-500" />
          Indicadores de Rendimiento
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
            <div className="text-2xl font-bold text-slate-900 dark:text-white mb-1">
              {dashboardData.progresoGeneral.toFixed(1)}%
            </div>
            <div className="text-sm text-slate-600 dark:text-slate-400">
              Tasa de Completado
            </div>
          </div>
          
          <div className="text-center p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
            <div className="text-2xl font-bold text-slate-900 dark:text-white mb-1">
              {dashboardData.totalColeccion > 0 ? Math.round(dashboardData.paginasTotales / dashboardData.totalColeccion) : 0}
            </div>
            <div className="text-sm text-slate-600 dark:text-slate-400">
              Páginas Promedio
            </div>
          </div>
          
          <div className="text-center p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
            <div className="text-2xl font-bold text-slate-900 dark:text-white mb-1">
              {dashboardData.totalColeccion > 0 ? (dashboardData.valorTotal / dashboardData.totalColeccion).toFixed(2) : 0}€
            </div>
            <div className="text-sm text-slate-600 dark:text-slate-400">
              Precio Promedio
            </div>
          </div>
          
          <div className="text-center p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
            <div className="text-2xl font-bold text-slate-900 dark:text-white mb-1">
              {dashboardData.sagasCompletadas + dashboardData.coleccionesMangaCompletadas}
            </div>
            <div className="text-sm text-slate-600 dark:text-slate-400">
              Series Completadas
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default StatisticsDashboard;